import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Ruler, Camera, Palette, ImageIcon,
  Globe, Store, Instagram, MessageCircle, QrCode, ImagePlus,
  Cpu, Database, Cloud, Activity, Shield
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO from '../../components/SEO';

// ─── Animation ────────────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }
  })
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: (delay = 0) => ({
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay }
  })
};

// ─── Hero Platform Diagram (compact 4-cards-around-hub) ────────────────────────
// Merges the old standalone "One platform. Four experiences." signature visual
// into the hero, sized to fit a column rather than a full-bleed section.
const HeroPlatformDiagram = () => {
  const cards = [
    { icon: Camera,    label: 'Virtual Try-On',    hint: 'See it on you',         pos: { left: '0%',  top: '0%',  align: 'items-start text-left' } },
    { icon: Ruler,     label: 'Smart Size',        hint: 'Right size, first time', pos: { right: '0%', top: '0%',  align: 'items-end text-right' } },
    { icon: ImageIcon, label: 'Lifestyle Moments', hint: 'See the moment',         pos: { left: '0%',  bottom: '0%', align: 'items-start text-left' } },
    { icon: Palette,   label: 'Color Change',      hint: 'Many looks',             pos: { right: '0%', bottom: '0%', align: 'items-end text-right' } },
  ];
  return (
    <div className="relative w-full max-w-[600px] mx-auto h-[340px] sm:h-[480px]" data-testid="hero-platform-diagram">
      {/* connector spokes */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 600 480" preserveAspectRatio="none">
        <defs>
          <linearGradient id="heroLine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[
          [110, 80, 300, 240],
          [490, 80, 300, 240],
          [110, 400, 300, 240],
          [490, 400, 300, 240],
        ].map(([x1, y1, x2, y2], i) => (
          <motion.line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="url(#heroLine)" strokeWidth="1.5" strokeDasharray="4 6"
            initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 + i * 0.1 }}
          />
        ))}
      </svg>

      {/* center hub */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-32 sm:h-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: 'backOut' }}
          className="relative w-full h-full rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 flex flex-col items-center justify-center shadow-[0_0_80px_rgba(59,130,246,0.5)]"
        >
          <motion.span
            className="absolute inset-0 rounded-2xl sm:rounded-3xl ring-2 ring-blue-300/50"
            animate={{ scale: [1, 1.18, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeOut' }}
          />
          <p className="text-white text-[10px] sm:text-xs font-bold tracking-[0.14em] sm:tracking-[0.18em]">NANOKART</p>
          <p className="text-blue-100 text-[8px] sm:text-[10px] font-semibold tracking-[0.14em] sm:tracking-[0.18em] mt-0.5 sm:mt-1">TRYON STUDIO</p>
        </motion.div>
      </div>

      {/* four corner cards */}
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`hero-platform-card-${i}`}
            className={`absolute w-[44%] rounded-xl sm:rounded-2xl bg-white/[0.04] backdrop-blur-md border border-white/10 p-2.5 sm:p-5 flex flex-col gap-1 sm:gap-1.5 ${c.pos.align}`}
            style={c.pos}
          >
            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-300" />
            </div>
            <p className="text-white font-semibold text-xs sm:text-base leading-tight">{c.label}</p>
            <p className="text-slate-400 text-[10px] sm:text-xs leading-snug">{c.hint}</p>
          </motion.div>
        );
      })}
    </div>
  );
};

// ─── Pills ────────────────────────────────────────────────────────────────────
const Pill = ({ children, icon: Icon, size = 'md', tone = 'light', testid }) => {
  const sizeCls = size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3.5 py-1.5 text-sm';
  const toneCls = tone === 'ghost'
    ? 'bg-transparent border-slate-200/80 text-slate-600 hover:text-blue-700'
    : 'bg-white border-slate-200 text-slate-700 shadow-sm hover:border-blue-200 hover:text-blue-700';
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full font-medium border transition-colors ${sizeCls} ${toneCls}`}>
      {Icon && <Icon className="w-3.5 h-3.5 text-blue-500" />}
      {children}
    </span>
  );
};

// ─── Empty placeholder (used inside each variant) ──────────────────────────────
const PlaceholderArt = ({ label }) => (
  <>
    <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-blue-200/40 blur-3xl" />
    <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-indigo-200/40 blur-3xl" />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <div className="w-12 h-12 rounded-2xl bg-white shadow-sm ring-1 ring-blue-100 flex items-center justify-center mb-3">
        <ImagePlus className="w-5 h-5 text-blue-500" />
      </div>
      <p className="text-[11px] font-semibold text-slate-500 tracking-[0.18em] uppercase">{label}</p>
      <p className="text-xs text-slate-400 mt-1">Visual coming soon</p>
    </div>
  </>
);

// ─── Variant 1: Image inside a polished card (Virtual Try-On) ──────────────────
const ImageCardVariant = ({ src, label, testid }) => (
  <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: '-80px' }} data-testid={testid} className="relative w-full">
    <div className="relative aspect-[5/4] w-full rounded-3xl overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 ring-1 ring-blue-100/80 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.25)]">
      {src ? <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" /> : <PlaceholderArt label={label} />}
    </div>
  </motion.div>
);

// ─── Variant 2: Transparent floating image (Smart Size) ────────────────────────
const FloatingImageVariant = ({ src, label, testid }) => (
  <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: '-80px' }} data-testid={testid} className="relative w-full">
    <div className="relative aspect-[5/4] w-full">
      {src ? (
        <img src={src} alt={label}
          className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_30px_50px_rgba(37,99,235,0.15)]" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 rounded-3xl bg-blue-50 ring-1 ring-blue-100 flex items-center justify-center mb-3 shadow-[0_20px_40px_-20px_rgba(37,99,235,0.4)]">
            <Ruler className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-[11px] font-semibold text-slate-500 tracking-[0.18em] uppercase">{label}</p>
          <p className="text-xs text-slate-400 mt-1">Visual coming soon</p>
        </div>
      )}
    </div>
  </motion.div>
);

// ─── Variant 3: Edge-to-edge photo, bleeds (Moments) ───────────────────────────
const BleedImageVariant = ({ src, label, testid }) => (
  <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: '-80px' }} data-testid={testid}
    className="relative w-full lg:-ml-16">
    <div className="relative aspect-[7/5] w-full rounded-2xl overflow-hidden ring-1 ring-blue-100/60 shadow-[0_40px_100px_-30px_rgba(15,23,42,0.35)]">
      {src ? (
        <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-blue-50 to-indigo-100">
          <PlaceholderArt label={label} />
        </div>
      )}
      <div className="absolute inset-0 ring-1 ring-inset ring-white/20 rounded-2xl pointer-events-none" />
    </div>
  </motion.div>
);

// ─── Variant 6: Abstract architecture (Infrastructure) ─────────────────────────
// If `src` is provided, render the image inside a polished card. Otherwise fall
// back to the abstract architecture SVG.
const ArchitectureVariant = ({ src = '', label, testid }) => {
  if (src) {
    return (
      <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
        viewport={{ once: true, margin: '-80px' }} data-testid={testid}
        className="relative w-full aspect-[5/4]">
        <div className="absolute inset-0 rounded-3xl overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40 ring-1 ring-blue-100/80 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.25)]">
          <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </motion.div>
    );
  }
  return (
  <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: '-80px' }} data-testid={testid}
    className="relative w-full aspect-[5/4]">
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-50 via-white to-blue-50/40 ring-1 ring-slate-100 overflow-hidden">
      <svg viewBox="0 0 400 320" className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="archLine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
        </defs>
        {/* connector lines */}
        {[
          [80, 80, 200, 160], [80, 240, 200, 160], [320, 80, 200, 160], [320, 240, 200, 160], [200, 40, 200, 160], [200, 280, 200, 160]
        ].map(([x1, y1, x2, y2], i) => (
          <motion.line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="url(#archLine)" strokeWidth="1.2" strokeDasharray="3 4"
            initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 0.7 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 + i * 0.05 }}
          />
        ))}
        {/* center hub */}
        <motion.circle cx="200" cy="160" r="34" fill="#2563eb"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'backOut' }} style={{ transformOrigin: '200px 160px' }} />
        <foreignObject x="188" y="148" width="24" height="24">
          <div style={{ color: '#ffffff' }}><Cpu size={24} /></div>
        </foreignObject>
      </svg>
      {/* node cards (HTML for crisp icons) */}
      {[
        { icon: Database, x: '20%', y: '25%', label: 'Cache' },
        { icon: Cloud,    x: '80%', y: '25%', label: 'Edge' },
        { icon: Activity, x: '20%', y: '75%', label: 'Analytics' },
        { icon: Shield,   x: '80%', y: '75%', label: 'Security' },
      ].map((n, i) => {
        const NIcon = n.icon;
        return (
          <motion.div key={i}
            initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 + i * 0.1, ease: 'backOut' }}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: n.x, top: n.y }}
          >
            <div className="w-12 h-12 rounded-2xl bg-white ring-1 ring-blue-100 shadow-[0_10px_25px_-10px_rgba(37,99,235,0.35)] flex items-center justify-center">
              <NIcon className="w-5 h-5 text-blue-600" />
            </div>
            <span className="mt-2 text-[10px] font-semibold text-slate-500 tracking-[0.14em] uppercase">{n.label}</span>
          </motion.div>
        );
      })}
    </div>
    {/* corner label */}
    <p className="absolute -bottom-6 left-0 text-[11px] font-semibold text-slate-400 tracking-[0.18em] uppercase">{label}</p>
  </motion.div>
  );
};

// ─── Variant 7: Dashboard mockup (Smart Gate) ──────────────────────────────────
const DashboardVariant = ({ src, label, testid }) => (
  <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: '-80px' }} data-testid={testid}
    className="relative w-full aspect-[5/4]">
    <div className="absolute inset-0 rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.35)]">
      {/* browser chrome */}
      <div className="h-7 sm:h-9 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 px-3 sm:px-4">
        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-slate-200" />
        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-slate-200" />
        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-slate-200" />
        <div className="ml-2 sm:ml-3 flex-1 h-4 sm:h-5 bg-white border border-slate-100 rounded-md" />
      </div>
      {src ? (
        <img src={src} alt={label} className="w-full h-[calc(100%-1.75rem)] sm:h-[calc(100%-2.25rem)] object-cover" />
      ) : (
        <div className="p-2.5 sm:p-5 grid grid-cols-3 gap-1.5 sm:gap-3 h-[calc(100%-1.75rem)] sm:h-[calc(100%-2.25rem)]">
          {/* top stat cards */}
          {[
            { v: '₹84.2k', vFull: '₹ 84,210', l: 'Spend', lFull: 'Spend today', tone: 'bg-blue-50 text-blue-700' },
            { v: '12.4k',  vFull: '12,409',   l: 'Try-ons', lFull: 'Try-ons', tone: 'bg-emerald-50 text-emerald-700' },
            { v: '0.4%',   vFull: '0.4%',     l: 'Abuse', lFull: 'Abuse blocked', tone: 'bg-rose-50 text-rose-700' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 + i * 0.07 }}
              className="rounded-md sm:rounded-xl border border-slate-100 p-1.5 sm:p-3 min-w-0"
            >
              <span className={`inline-block whitespace-nowrap text-[8px] sm:text-[10px] font-bold uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded ${s.tone}`}>
                <span className="sm:hidden">{s.l}</span>
                <span className="hidden sm:inline">{s.lFull}</span>
              </span>
              <p className="mt-1 sm:mt-2 text-xs sm:text-lg font-bold text-slate-900 whitespace-nowrap leading-tight">
                <span className="sm:hidden">{s.v}</span>
                <span className="hidden sm:inline">{s.vFull}</span>
              </p>
            </motion.div>
          ))}
          {/* chart placeholder */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}
            className="col-span-2 rounded-md sm:rounded-xl border border-slate-100 p-1.5 sm:p-3 min-w-0 flex flex-col"
          >
            <p className="text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 sm:mb-2 whitespace-nowrap">Daily wallet usage</p>
            <svg viewBox="0 0 200 60" preserveAspectRatio="none" className="w-full flex-1 min-h-0">
              <defs>
                <linearGradient id="dashArea" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,50 C30,30 60,40 90,25 C120,12 150,30 200,15 L200,60 L0,60 Z" fill="url(#dashArea)" />
              <path d="M0,50 C30,30 60,40 90,25 C120,12 150,30 200,15" fill="none" stroke="#2563eb" strokeWidth="1.8" />
            </svg>
          </motion.div>
          {/* limit gauge */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.35 }}
            className="rounded-md sm:rounded-xl border border-slate-100 p-1.5 sm:p-3 min-w-0 flex flex-col"
          >
            <p className="text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Monthly cap</p>
            <p className="mt-0.5 sm:mt-1 text-xs sm:text-base font-bold text-slate-900">62%</p>
            <div className="mt-auto h-1 sm:h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full w-[62%] bg-blue-500 rounded-full" />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  </motion.div>
);

// ─── Story section wrapper (alternating layout, variable media slot) ───────────
const Story = ({
  imageLeft = true,
  eyebrow,
  headline,
  sub,
  pills = [],
  pillSize = 'md',
  pillTone = 'light',
  media,
  bgClass = 'bg-white',
  textColor = 'text-slate-900',
  subColor = 'text-slate-500',
  testid,
  sectionId,
}) => {
  const mediaBlock = <div className="lg:col-span-6">{media}</div>;
  const textBlock = (
    <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
      viewport={{ once: true, margin: '-80px' }} className="lg:col-span-6">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-5">
          <span className="w-6 h-px bg-blue-300" />
          {eyebrow}
        </div>
      )}
      <h2 className={`text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight ${textColor}`}>
        {headline}
      </h2>
      <p className={`mt-4 sm:mt-6 text-base sm:text-xl leading-relaxed max-w-xl ${subColor}`}>{sub}</p>
      {pills.length > 0 && (
        <div className="mt-7 flex flex-wrap gap-2">
          {pills.map((p, i) => (
            <Pill key={i} icon={p.icon} size={pillSize} tone={pillTone} testid={`${testid}-pill-${i}`}>{p.label}</Pill>
          ))}
        </div>
      )}
    </motion.div>
  );
  return (
    <section id={sectionId} data-testid={testid} className={`py-16 sm:py-24 lg:py-32 ${bgClass}`}>
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-20 items-center">
          {imageLeft ? <>{mediaBlock}{textBlock}</> : <>{textBlock}{mediaBlock}</>}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Solutions = () => {
  return (
    <MarketingLayout>
      <SEO
        title="Solutions | AI Shopping Solutions for Fashion Commerce | Nanokart"
        description="AI shopping solutions for fashion commerce. Virtual Try-On, Smart Size Recommendation, Lifestyle Moments, and Color Change. Tuned on Indian silhouettes."
        keywords={['fashion AI platform', 'virtual try-on solution', 'AI size recommendation', 'lifestyle moments AI', 'AI color change fashion']}
        canonicalUrl="https://nanokart.ai/solutions"
      />

      {/* ── 1. HERO ──────────────────────────────────────────────────────────── */}
      <section className="pt-20 sm:pt-32 pb-14 sm:pb-28 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" data-testid="solutions-hero">
        {/* blue glows */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-blue-500/10 blur-3xl" />
          <div className="absolute top-1/2 right-0 translate-x-1/3 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-indigo-500/15 blur-3xl" />
        </div>
        {/* faint grid */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />

        <div className="container mx-auto px-4 sm:px-6 max-w-6xl relative">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-blue-200 text-xs font-semibold mb-6 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5" />
                The Complete AI Stack
              </div>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] sm:leading-[1.02] tracking-tight">
                AI Shopping<br/>Solutions for<br/><span className="text-blue-400">Fashion Commerce</span>
              </h1>
              <p className="mt-5 sm:mt-7 text-base sm:text-xl text-slate-300/90 leading-relaxed max-w-xl">
                Virtual Try-On, Smart Size, Moments and Color Change. Everywhere your customers shop.
              </p>
              <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" data-testid="hero-book-demo-btn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-400 transition-all shadow-[0_10px_40px_-10px_rgba(59,130,246,0.6)]">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#virtual-tryon" data-testid="hero-explore-solutions-btn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/[0.06] text-white rounded-full font-semibold border border-white/15 hover:bg-white/[0.10] hover:border-white/25 transition-all backdrop-blur-sm">
                  Explore Solutions
                </a>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.2}>
              <HeroPlatformDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. BUILT FOR REAL FASHION (moved up - the differentiator hook) ──── */}
      <section className="py-20 sm:py-28 lg:py-36 bg-white relative overflow-hidden" data-testid="section-real-fashion">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] bg-blue-50/40 rounded-full blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl relative text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-5 sm:mb-6">
              <span className="w-6 h-px bg-blue-300" />
              Built for Real Fashion
              <span className="w-6 h-px bg-blue-300" />
            </div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight">
              Complex garments. Real silhouettes.
            </h2>
            <p className="mt-5 sm:mt-7 text-base sm:text-xl text-slate-500 leading-relaxed max-w-2xl mx-auto">
              Photorealistic AI that preserves fabric details, natural draping, and diverse body proportions.
            </p>
          </motion.div>
          <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: '-80px' }} custom={0.15}
            className="mt-10 sm:mt-14 flex flex-wrap justify-center gap-2 sm:gap-3"
            data-testid="real-fashion-pills">
            {['Fabric Details', 'Natural Drapes', 'Diverse Silhouettes', 'Photorealistic Results'].map((label, i) => (
              <Pill key={i} testid={`real-fashion-pill-${i}`}>{label}</Pill>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 3. VIRTUAL TRY-ON · image-card variant ──────────────────────────── */}
      <Story
        sectionId="virtual-tryon" testid="section-virtual-tryon"
        imageLeft={true}
        eyebrow="Virtual Try-On"
        headline={<>See it<br/>on you.</>}
        sub="Upload a photo. Pick a dress. Instantly preview the fit."
        pills={[{ label: 'Photorealistic' }, { label: 'Natural folds' }, { label: 'Body proportions' }]}
        media={<ImageCardVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614191537.webp" label="Virtual Try-On preview" testid="vto-media" />}
        bgClass="bg-white"
      />

      {/* ── 4. SMART SIZE · transparent floating image, smaller ghost pills ──── */}
      <Story
        sectionId="smart-size" testid="section-smart-size"
        imageLeft={false}
        eyebrow="Smart Size Recommendation"
        headline={<>Right size.<br/>First time.</>}
        sub="AI-powered size recommendations with high confidence."
        pills={[{ label: 'Confidence Score' }, { label: 'Brand Size Charts' }, { label: 'Fewer Returns' }]}
        pillSize="sm" pillTone="ghost"
        media={<FloatingImageVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614193451.webp" label="Smart Size Recommendation" testid="size-media" />}
        bgClass="bg-gradient-to-b from-slate-50/60 to-white"
      />

      {/* ── 5. LIFESTYLE MOMENTS · edge-to-edge photo, bleeds outside container */}
      <Story
        sectionId="moments" testid="section-moments"
        imageLeft={true}
        eyebrow="Lifestyle Moments"
        headline={<>See the<br/>moment.</>}
        sub="Experience the same outfit across real-life settings."
        pills={[{ label: 'Work' }, { label: 'Travel' }, { label: 'Date Night' }, { label: 'Festive' }]}
        media={<BleedImageVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614194356.webp" label="Lifestyle Moments" testid="moments-media" />}
        bgClass="bg-white overflow-hidden"
      />

      {/* ── 6. COLOR CHANGE · 3 floating overlapping cards ──────────────────── */}
      <Story
        sectionId="color-change" testid="section-color-change"
        imageLeft={false}
        eyebrow="Color Change"
        headline={<>One dress.<br/>Many looks.</>}
        sub="Visualize every color variation instantly."
        pills={[{ label: 'Unlimited Variants' }, { label: 'No Reshoot' }, { label: 'Fabric Safe' }]}
        media={<ImageCardVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614194737.webp" label="Color variations" testid="color-media" />}
        bgClass="bg-gradient-to-b from-slate-50/60 to-white"
      />

      {/* ── 7. OMNICHANNEL · device collage ─────────────────────────────────── */}
      <Story
        sectionId="omnichannel" testid="section-omnichannel"
        imageLeft={true}
        eyebrow="Omnichannel Commerce"
        headline={<>Meet your customer<br/>everywhere.</>}
        sub="One inventory. One experience. Every channel."
        pills={[
          { label: 'Website', icon: Globe },
          { label: 'Store', icon: Store },
          { label: 'Instagram', icon: Instagram },
          { label: 'WhatsApp', icon: MessageCircle },
          { label: 'QR', icon: QrCode },
        ]}
        pillSize="sm"
        media={<ImageCardVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614200133.webp" label="Omnichannel deployment" testid="omni-media" />}
        bgClass="bg-white"
      />

      {/* ── 9. ENTERPRISE INFRASTRUCTURE · abstract architecture SVG ────────── */}
      <Story
        sectionId="enterprise" testid="section-enterprise"
        imageLeft={false}
        eyebrow="Enterprise Infrastructure"
        headline={<>Built<br/>for scale.</>}
        sub="Enterprise-grade infrastructure without enterprise complexity."
        pills={[
          { label: 'Intelligent Caching' },
          { label: 'Dedicated Infrastructure' },
          { label: 'Analytics' },
          { label: 'Watermarking' },
        ]}
        media={<ArchitectureVariant src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-website/20260614201719.webp" label="Infrastructure" testid="infra-media" />}
        bgClass="bg-gradient-to-b from-slate-50/60 to-white"
      />

      {/* ── 10. SMART GATE · dashboard mockup ───────────────────────────────── */}
      <Story
        sectionId="smart-gate" testid="section-smart-gate"
        imageLeft={true}
        eyebrow="Smart Gate"
        headline={<>Spend with<br/>confidence.</>}
        sub="Control budgets. Prevent abuse. Scale safely."
        pills={[
          { label: 'Budget Controls' },
          { label: 'Rate Limits' },
          { label: 'Abuse Protection' },
          { label: 'Smart Gate™' },
        ]}
        media={<DashboardVariant src="" label="Smart Gate dashboard" testid="smartgate-media" />}
        bgClass="bg-white"
      />

      {/* ── 11. TRANSPARENT PRICING (blue) ──────────────────────────────────── */}
      <section className="py-20 sm:py-28 lg:py-36 bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 relative overflow-hidden" data-testid="section-pricing">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, #ffffff33 0, transparent 40%), radial-gradient(circle at 80% 80%, #ffffff33 0, transparent 40%)'
        }} />
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl relative">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: '-80px' }} className="text-center">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-100 mb-5 sm:mb-6">
              <span className="w-6 h-px bg-blue-200/60" />
              Transparent Pricing
              <span className="w-6 h-px bg-blue-200/60" />
            </div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              Transparent pricing.<br/>Zero surprises.
            </h2>
            <p className="mt-5 sm:mt-6 text-base sm:text-xl text-blue-100/90 leading-relaxed max-w-2xl mx-auto">
              Nanokart charges only for monthly subscription and per API usage.
            </p>
          </motion.div>
          <div className="mt-10 sm:mt-14 lg:mt-16 grid sm:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} custom={0.05} data-testid="pricing-card-subscription"
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100 mb-2 sm:mb-3">Subscription</p>
              <p className="text-xl sm:text-3xl font-bold leading-tight">Predictable monthly fee.</p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} custom={0.1} data-testid="pricing-card-api-usage"
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100 mb-2 sm:mb-3">API Usage</p>
              <p className="text-xl sm:text-3xl font-bold leading-tight">Pay only for what you render.</p>
            </motion.div>
          </div>
          <div className="mt-12 sm:mt-16 text-center">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-blue-100 mb-4 sm:mb-5">Everything else included</p>
            <motion.div variants={fadeIn} initial="hidden" whileInView="visible"
              viewport={{ once: true }} custom={0.05}
              className="flex flex-wrap justify-center gap-2 sm:gap-2.5" data-testid="pricing-included-pills">
              {['Analytics', 'QR Commerce', 'SDK', 'Watermarking', 'Smart Gate™', 'Team Seats'].map((label, i) => (
                <span key={i} data-testid={`pricing-included-pill-${i}`}
                  className="inline-flex items-center px-3 sm:px-3.5 py-1 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-medium text-white">
                  {label}
                </span>
              ))}
            </motion.div>
          </div>
          <div className="mt-10 sm:mt-14 text-center">
            <Link to="/pricing" data-testid="pricing-see-full-btn"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 bg-white text-blue-700 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-lg">
              See full pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── 12. FINAL CTA (blue) ────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 lg:py-32 bg-blue-600 relative overflow-hidden" data-testid="section-final-cta">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, #ffffff22 0, transparent 60%)'
        }} />
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center relative">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              Ready to launch AI<br/>shopping experiences?
            </h2>
            <p className="mt-5 sm:mt-6 text-base sm:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Go live in days. Online, in-store and social.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" data-testid="final-cta-book-demo-btn"
                className="inline-flex items-center justify-center gap-2 px-7 sm:px-8 py-3.5 sm:py-4 bg-white text-blue-700 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-xl">
                Book a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/developer" data-testid="final-cta-developer-portal-btn"
                className="inline-flex items-center justify-center gap-2 px-7 sm:px-8 py-3.5 sm:py-4 bg-blue-700 text-white rounded-full font-semibold border border-blue-400 hover:bg-blue-800 transition-all">
                Visit Developer Portal
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default Solutions;
